import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Observable, of, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, take, tap, withLatestFrom, filter, zip } from 'rxjs/operators';

import { GMBActions } from '@actions/index';
import * as fromApp from '@reducers/index';
import { GMBService } from '@services/gmb/gmb.service';

@Injectable()
export class GMBEffects {
  constructor(
    private actions$: Actions,
    private store: Store<fromApp.State>,
    private gmbService: GMBService,
  ) {
  }

  // if the logged-in userId is different before from after this Observable
  // resolves, the authentication result will be **ignored**
  authenticateWithGoogle$ = createEffect(() => this.actions$.pipe(
    ofType(GMBActions.authenticateWithGoogle),
    switchMap(({ code }) => this.store.select(fromApp.getLoggedUser).pipe(
      take(1),
      map((user) => ({ userId: user.id, code }))
    )),
    switchMap(({ userId, code }) => this.gmbService.login(userId, code).pipe(
      withLatestFrom(this.store.select(fromApp.getLoggedUser)),
      take(1),
      // only accept results if the GMR userId hasn't changed while GMB was authenticating
      filter(([_, user]) => user.id === userId),
      map(([success, _]) => success),
      map(success => !!success
        ? GMBActions.searchGoogleBusinessesByCurrentUser()
        : GMBActions.error(null)
      ),
      catchError(error => of(GMBActions.error({message:error.message, cause: error.error.error})))
    ))
  ));

  refreshBusinesses$ = createEffect(() => this.actions$.pipe(
    ofType(GMBActions.refreshBusinesses),
    switchMap(() => this.store.select(fromApp.getLoggedUser).pipe(
      take(1),
      map((user) => ({ userId: user.id }))
    )),
    switchMap(({ userId }) => this.gmbService.refreshBusinesses(userId).pipe(
      map(businesses => GMBActions.searchGoogleBusinessesByCurrentUserSuccess({ businesses })),
      catchError(error => of(GMBActions.error({message:error.message, cause: error.error.error})))
    ))
  ));

  searchGoogleBusinessesByCurrentUser$ = createEffect(() => this.actions$.pipe(
    ofType(GMBActions.searchGoogleBusinessesByCurrentUser),
    switchMap(() => this.store.select(fromApp.getLoggedUser).pipe(
      take(1),
      map((user) => ({ userId: user.id || '' })),
    )),
    switchMap(({ userId }) => this.gmbService.businesses(userId).pipe(
      map(businesses => GMBActions.searchGoogleBusinessesByCurrentUserSuccess({ businesses })),
      catchError(error => of(GMBActions.error({message: error.message, cause: error.error.error})))
    ))
  ));
}
